import NestedTabNavigation from '@atoms/NestedTabNavigation';
import { useCallback, useEffect, useMemo } from 'react';
import EventOverview from './EventOverview';
import RegisteredAthletesTable from './RegisteredAthletesTable';
import useEventContext from '@hooks/useEventContext';
import LoadingPaper from '@atoms/LoadingPaper';
import useUser from '@hooks/useUser';
import EventPaymentConfig from './EventPaymentConfig';
import DivisionManagerPage from './DivisionManagerPage';
import { FormProvider, useForm } from 'react-hook-form';
import { getDefaultNewEvent, EventForm } from '@components/eventCreation/CreateEventUtils';
import EventPaymentHistory from './EventPaymentHistory';
import EventPermissionsTable from './EventPermissionsTable';
import WaiverForm from '@components/eventCreation/WaiverForm';
import useStripeAccount from '@hooks/useStripeAccount';
import FailedPayments from '@pages/Wrestlers/FailedPayments';
import { STATUS } from '@type/db-types';

export default function EventDetail() {
    const { event, isEventAdmin, loading } = useEventContext();
    const { stripeInfo } = useStripeAccount();
    const { user } = useUser();

    const formMethods = useForm<EventForm>({
        defaultValues: getDefaultNewEvent(
            event || undefined,
            stripeInfo?.detailsSubmitted,
            user?.id,
        ),
        mode: 'all',
    });

    useEffect(() => {
        // Reset form when event changes or finishes loading
        if (!loading && event)
            formMethods.reset(getDefaultNewEvent(event, stripeInfo?.detailsSubmitted, user?.id));
    }, [event, formMethods, loading, user?.id, stripeInfo?.detailsSubmitted]);

    const tabs = useMemo(() => {
        return event?.id
            ? [
                  {
                      tabName: 'General',
                      renderFn: () => <EventOverview />,
                  },
                  {
                      tabName: 'Registration',
                      renderFn: () => (
                          <RegisteredAthletesTable
                              event={event}
                              adminView={isEventAdmin}
                              statuses={[STATUS.ACTIVE]}
                          />
                      ),
                  },
                  {
                      tabName: 'Divisions & Weight Classes',
                      renderFn: () => <DivisionManagerPage />,
                  },
                  {
                      tabName: 'Waiver',
                      renderFn: () => <WaiverForm editable={isEventAdmin} />,
                  },
                  ...(isEventAdmin
                      ? [
                            {
                                tabName: 'Payment Config',
                                renderFn: () => <EventPaymentConfig />,
                            },
                            {
                                tabName: 'Payment History',
                                renderFn: () => <EventPaymentHistory />,
                            },
                            {
                                tabName: 'Event Permissions',
                                renderFn: () => <EventPermissionsTable />,
                            },
                            {
                                tabName: 'Failed Registrations',
                                renderFn: () => <FailedPayments />,
                            },
                        ]
                      : []),
              ]
            : [];
    }, [event, isEventAdmin]);

    const confirmTabChange = useCallback(() => {
        // if this is buggy, we can pull "dirtyFields" instead and cross-reference with the selected tab
        if (isEventAdmin && formMethods.formState.isDirty) {
            const discardChanges = confirm(
                'You have unsaved changes. Are you sure you want to leave?',
            );
            if (discardChanges) formMethods.reset();
            return discardChanges;
        }
        return true;
    }, [formMethods, isEventAdmin]);

    return (
        <LoadingPaper
            elevation={2}
            loading={loading}
            sx={{ padding: '1rem', margin: '1rem', elevation: 2 }}
        >
            <FormProvider {...formMethods}>
                <form>
                    <NestedTabNavigation tabs={tabs} confirmTabChange={confirmTabChange} />
                </form>
            </FormProvider>
        </LoadingPaper>
    );
}
